import { createSelector } from 'reselect';
import { getFormValues, isInvalid } from 'redux-form';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { DATE_FORMAT, FORM_NAME } from './constants';
import { CheckIsRegular } from './utils';

const selectOrderGroupsModalDomain = state => state.orderGroupsModalReducer || {};

const selectRouterDomain = state => state.router || {};

export const getOrderGroupInfoSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ orderGroupInfo }) => {
		if (!orderGroupInfo) return [];
		const data = orderGroupInfo;

		const rows = [];

		const getSubValue = value => (value !== null && value !== undefined ? value : '---');

		// Тип группировки
		if (data.type?.name) {
			rows.push({ id: 'type', title: 'Тип группировки', value: data.type.name });
		}

		// Периодичность маршрута
		if (data.period?.name) {
			rows.push({ id: 'period', title: 'Периодичность маршрута', value: data.period.name });
		}

		// Адреса отгрузки
		if (Array.isArray(data.points) && data.points.length > 0) {
			data.points
				.filter(el => el.type === 'from')
				.forEach((point, idx) => {
					if (point?.address) {
						const date = point.date ? new Date(point.date).toLocaleDateString('ru-RU') : undefined;
						rows.push({
							id: `address_from_${idx + 1}`,
							title: `Адрес отгрузки #${idx + 1}`,
							value: point.address,
							...(date && { date }),
						});
					}
				});
		}

		// Адреса доставки
		if (Array.isArray(data.points) && data.points.length > 0) {
			data.points
				.filter(el => el.type === 'to')
				.forEach((point, idx) => {
					if (point?.address) {
						const date = point.date ? new Date(point.date).toLocaleDateString('ru-RU') : undefined;
						rows.push({
							id: `address_to_${idx + 1}`,
							title: `Адрес доставки #${idx + 1}`,
							value: point.address,
							...(date && { date }),
						});
					}
				});
		}

		// Масса
		if (data.weight_from !== null || data.weight_to !== null) {
			let value = '';
			if (data.weight_from !== null && data.weight_to !== null) {
				value = `от ${data.weight_from} до ${data.weight_to} кг`;
			} else if (data.weight_from !== null) {
				value = `от ${data.weight_from} кг`;
			} else if (data.weight_to !== null) {
				value = `до ${data.weight_to} кг`;
			}
			rows.push({ id: 'weight_range', title: 'Масса', value });
		}

		// Объем
		if (data.volume_from !== null || data.volume_to !== null) {
			let value = '';
			if (data.volume_from !== null && data.volume_to !== null) {
				value = `от ${data.volume_from} до ${data.volume_to} м³`;
			} else if (data.volume_from !== null) {
				value = `от ${data.volume_from} м³`;
			} else if (data.volume_to !== null) {
				value = `до ${data.volume_to} м³`;
			}
			rows.push({ id: 'volume_range', title: 'Объем', value });
		}

		// Температурные режимы
		if (Array.isArray(data.modes)) {
			if (data.modes.length > 0) {
				rows.push({
					id: 'modes',
					title: 'Температурные режимы',
					value: data.modes.map(m => `${m.name} ${m.id !== 1 ? '°C' : ''}`).join(', '),
				});
			}
		}

		// Атрибуты
		if (data.attr) {
			rows.push({
				id: 'attr',
				title: 'Атрибуты',
				value: data.attr,
			});
		}

		// Конфигурация планирования
		if (data.config?.name) {
			rows.push({ id: 'config', title: 'Конфигурация планирования', value: data.config.name });
		}

		// Использовать автопарки
		if (data.is_autopark !== null) {
			rows.push({
				id: 'is_autopark',
				title: 'Использовать автопарки',
				value: `${data.is_autopark ? 'Да' : 'Нет'} `,
			});
		}

		// Доступные автопарки
		if (Array.isArray(data.autoparks)) {
			if (data.autoparks.length > 0) {
				rows.push({
					id: 'autoparks',
					title: 'Доступные автопарки',
					value: data.autoparks.map(m => `${m.tk.name}${m.name ? `, ${m.name}` : ''}`).join('; '),
				});
			}
		}

		// Тариф автопарка
		if (data.tariff_type?.name) {
			rows.push({ id: 'tariff_type', title: 'Тариф автопарка', value: data.tariff_type.name });
		}

		// Тип транспорта
		if (data.transport_type?.name) {
			rows.push({ id: 'transport_type', title: 'Тип транспорта', value: data.transport_type.name });
		}

		// Кол-во доступного авто
		if (data.transport_count) {
			rows.push({ id: 'transport_count', title: 'Кол-во доступного авто', value: `${data.transport_count} шт` });
		}

		// Грузоподъемность авто
		if (data.weight !== null && !data.is_autopark) {
			rows.push({
				id: 'weight',
				title: 'Грузоподъемность авто',
				value: data.weight !== null ? `${data.weight} кг` : '---',
				sub_value: getSubValue(data.weight_recycling_rate),
			});
		}

		// Объем кузова авто
		if (data.volume !== null && !data.is_autopark) {
			rows.push({
				id: 'volume',
				title: 'Объем кузова авто',
				value: data.volume !== null ? `${data.volume} м³` : '---',
				sub_value: getSubValue(data.volume_recycling_rate),
			});
		}

		// Коэффициент утилизации по массе
		if (data.weight_recycling_rate !== null && data.is_autopark) {
			rows.push({
				id: 'weight_recycling_rate',
				title: 'Коэффициент утилизации по массе',
				value: `${data.weight_recycling_rate}`,
			});
		}

		// Коэффициент утилизации по объему
		if (data.volume_recycling_rate !== null && data.is_autopark) {
			rows.push({
				id: 'volume_recycling_rate',
				title: 'Коэффициент утилизации по объему',
				value: `${data.volume_recycling_rate}`,
			});
		}

		// Коэффициент массы груза
		if (data.weight_rate !== null) {
			rows.push({
				id: 'weight_rate',
				title: 'Коэффициент массы груза',
				value: `${data.weight_rate}`,
			});
		}

		// Коэффициент объема груза
		if (data.volume_rate !== null) {
			rows.push({
				id: 'volume_rate',
				title: 'Коэффициент объема груза',
				value: `${data.volume_rate}`,
			});
		}

		// Максимальное кол. точек
		if (data.points_amount !== null) {
			rows.push({
				id: 'points_amount',
				title: 'Максимальное кол. точек',
				value: `${data.points_amount} шт`,
			});
		}

		// Коэффициент скорости
		if (data.speed_multiplier !== null) {
			rows.push({
				id: 'speed_multiplier',
				title: 'Коэффициент скорости',
				value: `${data.speed_multiplier}`,
			});
		}

		// Рабочая смена водителя
		if (data.work_time_from !== null && data.work_time_to !== null) {
			rows.push({
				id: 'work_time_from',
				title: 'Рабочая смена водителя',
				value: `${data.work_time_from} - ${data.work_time_to}`,
			});
		}

		// Окно отгрузки
		if (data.pickup_time_from !== null && data.pickup_time_to !== null) {
			rows.push({
				id: 'pickup_time_from',
				title: 'Окно отгрузки',
				value: `${data.pickup_time_from} - ${data.pickup_time_to}`,
			});
		}

		// Время отгрузки со склада
		if (data.pickup_time !== null) {
			rows.push({
				id: 'pickup_time',
				title: 'Время отгрузки со склада',
				value: `${data.pickup_time} мин`,
			});
		}

		// Окно доставки
		if (data.drop_time_from !== null && data.drop_time_to !== null) {
			rows.push({
				id: 'drop_time_from',
				title: 'Окно доставки',
				value: `${data.drop_time_from} - ${data.drop_time_to}`,
			});
		}

		// Время разгрузки у клиента
		if (data.drop_time !== null) {
			rows.push({
				id: 'drop_time',
				title: 'Время разгрузки у клиента',
				value: `${data.drop_time} мин`,
			});
		}

		// Совместимость температурных режимов
		if (data.is_compatibility_mode !== null) {
			rows.push({
				id: 'is_compatibility_mode',
				title: 'Совместимость температурных режимов',
				value: `${data.is_compatibility_mode ? 'Да' : 'Нет'} `,
			});
		}

		if (Array.isArray(data.compatibilities) && data.compatibilities.length > 0) {
			data.compatibilities.forEach((compatibility, idx) => {
				rows.push({
					id: `compatibilities_${idx + 1}`,
					title: `Совместимость #${idx + 1}`,
					value: compatibility?.mode?.name_with_symbol ?? '---',
					sub_value: getSubValue(compatibility.compatibility.map(item => item.name_with_symbol).join(', ')),
				});
			});
		}

		// Учесть километраж возврата на склад
		if (data.is_refund !== null) {
			rows.push({
				id: 'is_refund',
				title: 'Учесть километраж возврата на склад',
				value: `${data.is_refund ? 'Да' : 'Нет'} `,
			});
		}

		// Отгрузка в день доставки
		if (data.is_pickup_day_to_day !== null) {
			rows.push({
				id: 'is_pickup_day_to_day',
				title: 'Отгрузка в день доставки',
				value: `${data.is_pickup_day_to_day ? 'Да' : 'Нет'} `,
			});
		}

		// Гибкая дата доставки
		if (data.is_flexible_date !== null) {
			rows.push({
				id: 'is_flexible_date',
				title: 'Гибкая дата доставки',
				value: `${data.is_flexible_date ? 'Да' : 'Нет'} `,
			});
		}

		// Одна отгрузка на одну машину
		if (data.is_ship_per_car !== null) {
			rows.push({
				id: 'is_ship_per_car',
				title: 'Одна отгрузка на одну машину',
				value: `${data.is_ship_per_car ? 'Да' : 'Нет'} `,
			});
		}

		// Учесть маршруты Заказчика
		if (data.is_allow_way !== null) {
			rows.push({
				id: 'is_allow_way',
				title: 'Учесть маршруты Заказчика',
				value: `${data.is_allow_way ? 'Да' : 'Нет'} `,
			});
		}

		// Кол. нарушений маршрута
		if (data.penalty_cost !== null) {
			rows.push({
				id: 'penalty_cost',
				title: 'Кол. нарушений маршрута',
				value: `${data.penalty_cost}`,
			});
		}

		// Отгрузка в выходные дни
		if (data.is_weekend_pickup !== null) {
			rows.push({
				id: 'is_weekend_pickup',
				title: 'Отгрузка в выходные дни',
				value: `${data.is_weekend_pickup ? 'Да' : 'Нет'} `,
			});
		}

		// Доставка в выходные дни
		if (data.is_weekend_drop !== null) {
			rows.push({
				id: 'is_weekend_drop',
				title: 'Доставка в выходные дни',
				value: `${data.is_weekend_drop ? 'Да' : 'Нет'} `,
			});
		}

		// Учесть пробки
		if (data.is_traffic_jams !== null) {
			rows.push({
				id: 'is_traffic_jams',
				title: 'Учесть пробки',
				value: `${data.is_traffic_jams ? 'Да' : 'Нет'} `,
			});
		}

		// Учесть платные дороги
		if (data.is_toll_roads !== null) {
			rows.push({
				id: 'is_toll_roads',
				title: 'Учесть платные дороги',
				value: `${data.is_toll_roads ? 'Да' : 'Нет'} `,
			});
		}

		// Маршрутизация от
		if (data.is_start_date !== null) {
			rows.push({
				id: 'is_start_date',
				title: 'Маршрутизация от',
				value: `${data.is_start_date ? 'Да' : 'Нет'} `,
			});
		}

		// Мин. дата отгрузки
		if (data.start_date !== null) {
			rows.push({
				id: 'start_date',
				title: 'Мин. дата отгрузки',
				value: `${moment(data.start_date).format(DATE_FORMAT)}`,
			});
		}

		// Учесть дату создания заказа
		if (data.is_created_date !== null) {
			rows.push({
				id: 'is_created_date',
				title: 'Учесть дату создания заказа',
				value: `${data.is_created_date ? 'Да' : 'Нет'} `,
			});
		}

		// Кол. дней на сборку
		if (data.build_days !== null) {
			rows.push({
				id: 'build_days',
				title: 'Кол. дней на сборку',
				value: `${data.build_days}`,
			});
		}

		return rows;
	});

export const getIsOrderGroupInfoLoadingSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ isOrderGroupInfoLoading, isOrderGroupDeleting }) => isOrderGroupInfoLoading || isOrderGroupDeleting,
	);

export const getIsOrderGroupSavingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isOrderGroupSaving }) => isOrderGroupSaving);

export const getIsOrderGroupDeletingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isOrderGroupDeleting }) => isOrderGroupDeleting);

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order-group');
	});

export const getActiveOrderGroupIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return query?.modal === 'order-group' ? query?.id : null;
	});

export const getDataNameSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ orderGroupInfo }) => {
		return orderGroupInfo?.data?.name || 'Группировка заказов';
	});

export const getOrderGroupFormValuesSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => ({
		...formValues,
	}));

export const getOrderGroupFormInitialValuesSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ orderGroupInfo }) => {
		if (!orderGroupInfo) {
			return {
				address_from: [{}],
				address_to: [{}],
				type_id: 1,
				work_time_from: '09:00',
				work_time_to: '18:00',
				pickup_time_from: '09:00',
				pickup_time_to: '18:00',
				pickup_time: 10,
				drop_time_from: '09:00',
				drop_time_to: '19:00',
				drop_time: 2,
				is_weekend_drop: true,
				is_traffic_jams: true,
				compatibilities: [],
			};
		}
		const data = orderGroupInfo;

		const isRegular = CheckIsRegular(data);

		const modes = data.modes.reduce((acc, mode) => {
			acc[mode.id] = true;
			return acc;
		}, {});

		let result = {};
		if (isRegular) {
			result = {
				name: data.name,
				type_id: data.type.id,
				address_from: data?.points[0]?.address,
				address_to: data?.points.slice(1).map(point => ({ address: point.address, days: point.days })),
				date: data?.points[0]?.date,
				modes,
				weight: data.weight,
				weight_recycling_rate: data.weight_recycling_rate,
				weight_rate: data.weight_rate,
				volume: data.volume,
				volume_recycling_rate: data.volume_recycling_rate,
				volume_rate: data.volume_rate,
				period_id: data.period.id,
			};
		} else {
			const addressFrom = data?.points.filter(el => el.type === 'from');

			const addressTo = data?.points.filter(el => el.type === 'to');

			result = {
				name: data.name,
				type_id: data.type.id,
				address_from: addressFrom.length !== 0 ? addressFrom : [{}],
				address_to: addressTo.length !== 0 ? addressTo : [{}],
				modes,
				config_id: data?.config?.id,
				is_autopark: data.is_autopark,
				autopark_ids: data?.autoparks?.map(el => el.id),
				tariff_type_id: data?.tariff_type?.id,
				transport_type_id: data?.transport_type?.id,
				transport_count: data?.transport_count,
				weight: data.weight,
				weight_recycling_rate: data.weight_recycling_rate,
				weight_rate: data.weight_rate,
				volume: data.volume,
				volume_recycling_rate: data.volume_recycling_rate,
				volume_rate: data.volume_rate,
				points_amount: data.points_amount,
				speed_multiplier: data.speed_multiplier,
				work_time_from: data.work_time_from,
				work_time_to: data.work_time_to,
				pickup_time_from: data.pickup_time_from,
				pickup_time_to: data.pickup_time_to,
				pickup_time: data.pickup_time,
				drop_time_from: data.drop_time_from,
				drop_time_to: data.drop_time_to,
				drop_time: data.drop_time,
				weight_from: data.weight_from,
				weight_to: data.weight_to,
				volume_from: data.volume_from,
				volume_to: data.volume_to,
				is_compatibility_mode: data.is_compatibility_mode,
				is_refund: data.is_refund,
				is_pickup_day_to_day: data.is_pickup_day_to_day,
				is_flexible_date: data.is_flexible_date,
				is_ship_per_car: data.is_ship_per_car,
				is_allow_way: data.is_allow_way,
				penalty_cost: data.penalty_cost,
				is_weekend_pickup: data.is_pickup_day_to_day ? true : data.is_weekend_pickup,
				is_weekend_drop: !data.is_flexible_date ? true : data.is_weekend_drop,
				is_traffic_jams: data.is_traffic_jams,
				is_toll_roads: data.is_toll_roads,
				is_start_date: data.is_start_date ?? false,
				start_date: data.start_date,
				is_created_date: data.is_created_date ?? false,
				build_days: data.build_days,
				attr: data.attr,
				compatibilities:
					data.compatibilities.map(compatibility => ({
						mode_id: compatibility.mode.id,
						compatibility: compatibility.compatibility.map(el => el.id),
					})) ?? [],
			};
		}

		return result;
	});

export const getAddressesSearchValueSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ addressesSearchValue }) => addressesSearchValue || {});

export const getIsAddressesLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isAddressesLoading }) => isAddressesLoading || {});

export const getPeriodsOptionsSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ periodsOptions }) => {
		if (!periodsOptions) return [];
		return [...periodsOptions.map(el => ({ ...el, text: el.name, value: el.id, key: String(el.id) }))];
	});

export const getIsPeriodsLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isPeriodsLoading }) => isPeriodsLoading || false);

export const getTypesSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ types }) => types?.map(t => ({ value: t.id, text: t.name, name: t.name })) || [],
	);

export const getIsTypesLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isTypesLoading }) => isTypesLoading);

export const getModesOptionsSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ modes }) => modes?.map(m => ({ id: m.id, name: m.name, name_with_symbol: m.name_with_symbol })) || [],
	);

export const getModesSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ modes }) => modes?.map(m => ({ value: m.id, text: m.name_with_symbol, name: m.name_with_symbol })) || [],
	);

export const getIsModesLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isModesLoading }) => isModesLoading);

export const getIsFormShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order-group' && query?.mode === 'add');
	});

export const getIsEditFormShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'order-group' && query?.mode === 'edit');
	});

export const getAddressesOptionsSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ addressesOptions }) => {
		if (!addressesOptions) return {};
		return Object.entries(addressesOptions).reduce((acc, [key, value]) => {
			acc[key] = value.map(el => ({ value: el.value, text: el.value, key: el.value }));

			return acc;
		}, {});
	});

export const getAddressesSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ addressesOptions }) => addressesOptions);

export const getOrderGroupFormDataForApiSelector = () =>
	createSelector(
		getFormValues(FORM_NAME),
		selectOrderGroupsModalDomain,
		(formValues, { addressesOptions, modes }) => {
			if (!formValues) return {};

			const {
				type_id,
				name,
				period_id,
				address_from,
				date,
				address_to,
				weight_from,
				weight_to,
				volume_from,
				volume_to,
				modes: modesFormValues,
				config_id,
				transport_type_id,
				weight,
				weight_recycling_rate,
				weight_rate,
				volume,
				volume_recycling_rate,
				volume_rate,
				points_amount,
				speed_multiplier,
				work_time_from,
				work_time_to,
				pickup_time_from,
				pickup_time_to,
				pickup_time,
				drop_time_from,
				drop_time_to,
				drop_time,
				is_compatibility_mode,
				is_refund,
				is_pickup_day_to_day,
				is_flexible_date,
				is_ship_per_car,
				is_allow_way,
				penalty_cost,
				is_weekend_pickup,
				is_weekend_drop,
				is_start_date,
				start_date,
				is_created_date,
				build_days,
				attr,
				compatibilities,
				transport_count,
				is_autopark,
				autopark_ids,
				tariff_type_id,
				is_traffic_jams,
				is_toll_roads,
			} = formValues;

			const isRegular = CheckIsRegular(formValues);

			const modeIds = [];
			modes.forEach(mode => {
				if (mode && modesFormValues && modesFormValues[mode.id]) {
					modeIds.push(mode.id);
				}
			});
			const pointsArr = [];

			const isAddressFromExists = address_from && address_from.length > 0 && addressesOptions;
			if (isAddressFromExists) {
				const addresses = [];
				address_from.forEach((point, index) => {
					const addressKey = `address_from[${index}].address`;

					const address = addressesOptions[addressKey]?.find(el => el.value === point.address);
					if (address) {
						if (!isRegular) {
							delete address?.data?.date;
						}
						addresses.push({
							address: point.address,
							type: 'from',
							...(address?.data && { ...address.data }),
							...(date && isRegular && { date }),
						});
					}
				});
				pointsArr.push(...addresses);
			}

			const isAddressToExists = address_to && address_to.length > 0 && addressesOptions;
			if (isAddressToExists) {
				const addresses = [];
				address_to.forEach((point, index) => {
					const addressKey = `address_to[${index}].address`;

					const address = addressesOptions[addressKey]?.find(el => el.value === point.address);
					if (address) {
						if (!isRegular) {
							delete address?.data?.date;
						}
						addresses.push({
							address: point.address,
							type: 'to',
							...(address?.data && { ...address.data }),
							...(isRegular && point.days && { days: point.days }),
						});
					}
				});
				pointsArr.push(...addresses);
			}

			return {
				type_id,
				name,
				...(pointsArr.length > 0 && { points: pointsArr }),
				mode_ids: modeIds,
				volume: volume === '' || is_autopark ? null : volume,
				weight: weight === '' || is_autopark ? null : weight,
				weight_recycling_rate: weight_recycling_rate === '' ? null : weight_recycling_rate,
				volume_recycling_rate: volume_recycling_rate === '' ? null : volume_recycling_rate,
				weight_rate: weight_rate === '' ? null : weight_rate,
				volume_rate: volume_rate === '' ? null : volume_rate,
				...(isRegular && {
					period_id: period_id || null,
				}),
				// Не регулярный маршрут
				...(!isRegular && {
					config_id: config_id === '' ? null : config_id,
					is_autopark: is_autopark === '' ? null : is_autopark,
					autopark_ids: autopark_ids === '' || !is_autopark ? null : autopark_ids,
					tariff_type_id: tariff_type_id === '' || !is_autopark ? null : tariff_type_id,
					transport_type_id: transport_type_id === '' || is_autopark ? null : transport_type_id,
					transport_count: transport_count === '' || is_autopark ? null : transport_count,
					points_amount: points_amount === '' ? null : points_amount,
					speed_multiplier: speed_multiplier === '' ? null : speed_multiplier,
					work_time_from: work_time_from === '' ? null : work_time_from,
					work_time_to: work_time_to === '' ? null : work_time_to,
					pickup_time_from: pickup_time_from === '' ? null : pickup_time_from,
					pickup_time_to: pickup_time_to === '' ? null : pickup_time_to,
					pickup_time: pickup_time === '' ? null : pickup_time,
					drop_time_from: drop_time_from === '' ? null : drop_time_from,
					drop_time_to: drop_time_to === '' ? null : drop_time_to,
					drop_time: drop_time === '' ? null : drop_time,
					weight_from: weight_from === '' ? null : weight_from,
					weight_to: weight_to === '' ? null : weight_to,
					volume_from: volume_from === '' ? null : volume_from,
					volume_to: volume_to === '' ? null : volume_to,
					is_compatibility_mode: is_compatibility_mode === '' ? null : is_compatibility_mode,
					is_refund: is_refund === '' ? null : is_refund,
					is_pickup_day_to_day: is_pickup_day_to_day === '' ? null : is_pickup_day_to_day,
					is_flexible_date: is_flexible_date === '' ? null : is_flexible_date,
					is_ship_per_car: is_ship_per_car === '' ? null : is_ship_per_car,
					is_allow_way: is_allow_way === '' ? null : is_allow_way,
					penalty_cost: penalty_cost === '' ? null : penalty_cost,
					is_weekend_pickup: is_weekend_pickup === '' ? null : is_weekend_pickup,
					is_weekend_drop: is_weekend_drop === '' ? null : is_weekend_drop,
					is_traffic_jams: is_traffic_jams === '' ? null : is_traffic_jams,
					is_toll_roads: is_toll_roads === '' ? null : is_toll_roads,
					is_start_date: is_start_date === '' ? null : is_start_date,
					start_date: start_date === '' ? null : start_date,
					is_created_date: is_created_date === '' ? null : is_created_date,
					build_days: build_days === '' ? null : build_days,
					attr: attr === '' ? null : attr,
					compatibilities: compatibilities ?? [],
				}),
			};
		},
	);

export const getOrderGroupFormIsValidSelector = () => createSelector(isInvalid(FORM_NAME), isInvalid => isInvalid);

export const getIsRegularSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		return formValues?.type_id === 2;
	});

export const getOrderNameSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ orderGroupInfo }) => {
		return orderGroupInfo?.name || '';
	});

export const getConfigsSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ configs }) => configs?.map(t => ({ value: t.id, text: t.name, name: t.name })) || [],
	);

export const getIsConfigsLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isConfigsLoading }) => isConfigsLoading);

export const getTransportTypesSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ transportTypes }) => transportTypes?.map(t => ({ value: t.id, text: t.name, name: t.name })) || [],
	);

export const getIsTransportTypesLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isTransportTypesLoading }) => isTransportTypesLoading);

export const getIsOrdersGroupsTrashedSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ orderGroupInfo }) => {
		return !!orderGroupInfo?.deleted_at;
	});

export const getTariffTypesSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ tariffTypes }) => tariffTypes?.map(t => ({ value: t.id, text: t.name, name: t.name })) || [],
	);

export const getIsTariffTypesLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isTariffTypesLoading }) => isTariffTypesLoading);

export const getAutoparksSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ autoparks }) =>
			autoparks?.map(t => ({
				value: t.id,
				text: `${t.tk.name}${t.name ? `, ${t.name}` : ''}`,
				name: `${t.tk.name}${t.name ? `, ${t.name}` : ''}`,
			})) || [],
	);

export const getIsAutoparksLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isAutoparksLoading }) => isAutoparksLoading);
